
import { useState } from "react";
import { EmailRecord } from "@/types/email";
import { Investor, Contact } from "@/types/investor";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { Search, Mail, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { getRelevanceBadgeVariant } from "@/utils/uiHelpers";

interface EmailsListProps {
  emails: EmailRecord[];
  investors: Investor[];
  contacts: Contact[];
  onViewEmail: (email: EmailRecord) => void;
  onDeleteEmail?: (email: EmailRecord) => void;
}

const EmailsList = ({ emails, investors, contacts, onViewEmail, onDeleteEmail }: EmailsListProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  
  const getInvestor = (investorId: string): Investor | undefined => {
    return investors.find(investor => investor.id === investorId);
  };
  
  const getContact = (contactId: string): Contact | undefined => {
    return contacts.find(contact => contact.id === contactId);
  };
  
  // Check if an investor has been processed
  const isInvestorProcessed = (investor: Investor | undefined): boolean => {
    return !!investor?.lastRelevanceUpdate;
  };
  
  const filteredEmails = emails.filter(email => {
    const investor = getInvestor(email.investorId);
    const contact = getContact(email.contactId);
    
    return (
      investor?.firm.toLowerCase().includes(searchTerm.toLowerCase()) ||
      investor?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });
  
  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
        <Input
          type="text"
          placeholder="Search by firm, investor name, or contact name..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Firm</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Sent Via</TableHead>
              <TableHead>Date Sent</TableHead>
              <TableHead>Response</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEmails.length > 0 ? (
              filteredEmails.map((email) => {
                const investor = getInvestor(email.investorId);
                const contact = getContact(email.contactId);
                const processed = isInvestorProcessed(investor);
                
                return (
                  <TableRow key={email.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {investor?.firm || "Unknown"}
                        {investor && processed ? (
                          <Badge variant={getRelevanceBadgeVariant(investor.relevanceScore)}>
                            {investor.relevanceScore.toFixed(1)}
                          </Badge>
                        ) : investor ? (
                          <Badge variant="outline" className="bg-gray-100 text-gray-500">
                            0.0
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>{contact?.name || "Unknown"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {email.sentVia || "Manual"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {email.sentAt ? format(new Date(email.sentAt), "MMM d, yyyy h:mm a") : "Unknown"}
                    </TableCell>
                    <TableCell>
                      {email.hasResponse ? (
                        <div className="flex items-center text-green-600">
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          {email.responseAt && format(new Date(email.responseAt), "MMM d, yyyy")}
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-400">
                          <XCircle className="h-4 w-4 mr-1" />
                          No response
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => onViewEmail(email)}
                          className="flex items-center gap-1"
                        >
                          <Mail className="h-3 w-3" />
                          View
                        </Button>
                        {onDeleteEmail && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => onDeleteEmail(email)}
                            className="flex items-center gap-1 text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="h-3 w-3" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  No emails found matching your search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default EmailsList;
